import { computed, onMounted, reactive, ref } from 'vue';
// @ts-ignore
import { FilterMatchMode } from '@primevue/core/api';
import { APIPagyResponse } from '../types';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_PAGE_NUMBER = 1;

// PrimeVue's on-sort event contract: s
// ortOrder is 1 (ascending), -1 (descending) or 0/undefined (unsorted)
const SORT_ORDER_ASC = 1;
const SORT_ORDER_DESC = -1;

const SORT_ASC = 'asc';
const SORT_DESC = 'desc';

export type PaginationTracking = {
  totalRecords: number;
  pageSize: number;
  pageNumber: number;
  pageCount: number | null;
  currentSortField: string;
  currentSortType: typeof SORT_ASC | typeof SORT_DESC | null;
};

export type SortChangeParams = {
  sortField: string;
  sortOrder: typeof SORT_ORDER_ASC | typeof SORT_ORDER_DESC | 0;
};

export type FilterChangeEvent = Array<[string, { value: any; matchMode?: string }]>;

/**
 * Vue 3 composable for handling data sent through pagy for paginated pages in the application.
 *
 * Expectations:
 * - Expects a `getRowData(reset?)` function to be passed in that will take care of actually going and getting
 *   the data from the server
 *
 * It provides:
 * - `records`: the rows to display in the current table
 * - `searchParams`: the filter parameters sent to the api
 * - `pagination`: tracking state - `totalRecords`, `pageSize`, `pageNumber`, `pageCount`, `currentSortField`,
 *   `currentSortType`
 * - `handlePageChange`, `handleSortChange`, `processPagyResponse`, `onFilterChange`
 */
export function usePagination<T>(options: {
  /** Fetches a page of data from the server; called after any state change that should reload the table. */
  getRowData: () => Promise<APIPagyResponse<T> | undefined>;
  /** Metadata describing which filters are multiselects, keyed the same way as the table's filter-values. */
  filterValues?: Record<string, { matchMode?: string }>;
  /** Called for multiselect filters so the caller can sync its own filter widget (e.g. via template refs). */
  onMultiSelectFilterChange?: (filterKey: string, value: any) => void;
}) {
  const { getRowData, filterValues = {}, onMultiSelectFilterChange } = options;

  const records = ref<T[]>([]);
  const searchParams = ref<Record<string, any>>({});
  const pagination: PaginationTracking = reactive({
    totalRecords: 0,
    pageSize: DEFAULT_PAGE_SIZE,
    pageNumber: DEFAULT_PAGE_NUMBER,
    pageCount: null,
    currentSortField: '',
    currentSortType: null
  });
  
  const toAPIPayload: Record<string, any> = computed<Record<string, any>>(() => {
    return { 
      ...searchParams,
      page: pagination.pageNumber,
      sort_by: pagination.currentSortField,
      sort_type: pagination.currentSortType
    };
  })

  function processPagyResponse<T = any>(response: APIPagyResponse<T> | undefined, processDataFn: ((data: T[]) => any[]) | null = null) {
    if(!response) return;

    records.value = processDataFn ? processDataFn(response.data) : response.data;
    if (response.pagy) {
      pagination.totalRecords = response.pagy.count;
      pagination.pageCount = response.pagy.last;
      pagination.pageNumber = response.pagy.page;
    }
  }

  /**
   * Sets search params and reloads the table; also notifies the caller to sync/clear multiselect
   * filters, since the composable has no direct access to the table's template refs.
   *
   * @param event [key, filter][] where key is the filter field and filter is { matchMode, value }
   */
  const onFilterChange = async(event: FilterChangeEvent) => {
    // Converts the incoming array to key value pairs
    const filters: Record<string, any> = {};
    if (event && event.length > 0) {
      event.forEach(([key, filter]) => {
        filters[key] = filter.value ? filter.value : '';
      });
    }
    // assign to the search params to send to the api
    searchParams.value = filters;

    if (event) {
      event.forEach(([key, filter]) => {
        if (filterValues[key]?.matchMode === FilterMatchMode.IN) {
          onMultiSelectFilterChange?.(key, filter.value);
        }
      });
    }

    // Load the data based on the search params
    // Go back to the first page
    // pagination.pageNumber = 1;
    processPagyResponse(await getRowData());
  }

    // sets the page number and reloads the data
  const onPageChange = async (page: number) => {
    pagination.pageNumber = page;
    processPagyResponse(await getRowData());
  }

  const onSortChange = async(params: SortChangeParams) => {
    pagination.currentSortField = params.sortField;
    switch (params.sortOrder) {
      case SORT_ORDER_ASC:
        pagination.currentSortType = SORT_ASC;
        break;
      case SORT_ORDER_DESC:
        pagination.currentSortType = SORT_DESC;
        break;
      default:
        pagination.currentSortType = null;
    }

    processPagyResponse(await getRowData());
  }

  //onMounted(async () => { processPagyResponse(await getRowData()) });

  return {
    records,
    searchParams,
    pagination,
    toAPIPayload,
    onPageChange,
    onSortChange,
    processPagyResponse,
    onFilterChange
  };
}
